/* import __COLOCATED_TEMPLATE__ from './sync-button.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import { SyncBehaviorType } from 'embercom/models/import-service/import-source';
import type ImportSource from 'embercom/models/import-service/import-source';
import { action } from '@ember/object';
import { SourceType } from 'embercom/models/import-service/import-source';
import { tracked } from '@glimmer/tracking';

interface Args {
  importSource: ImportSource;
  contentId: string;
  tooltipKey?: string;
  onSyncSuccess?: () => void;
  onSyncError?: () => void;
}

export default class SyncButton extends Component<Args> {
  @service declare intl: IntlService;
  @service declare notificationsService: any;
  @service declare knowledgeHubService: KnowledgeHubService;
  @tracked isSyncInProgress = false;
  @tracked buttonLabel = '';

  constructor(owner: any, args: Args) {
    super(owner, args);
    this.isSyncInProgress = this.args.importSource?.isInProgress;
    this.buttonLabel = this.isSyncInProgress
      ? this.intl.t('knowledge-hub.content-editor.pdf.sync.button.syncing')
      : this.intl.t('knowledge-hub.content-editor.pdf.sync.button.sync');
  }

  get tooltipText() {
    let sourceType = this.args.importSource?.sourceType;
    return `knowledge-hub.content-editor.pdf.sync.tooltip.${SourceType[sourceType]}`.toLowerCase();
  }

  @action
  async syncDocumentContent(): Promise<void> {
    let { importSource, contentId } = this.args;
    this.isSyncInProgress = true;
    this.buttonLabel = this.intl.t('knowledge-hub.content-editor.pdf.sync.button.syncing');

    if (!importSource?.id) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.content-editor.pdf.sync.failure-message.no-source'),
      );
      return;
    }

    try {
      await this.knowledgeHubService.syncSelectedEntities(
        importSource.id.toString(),
        [Number(contentId)],
        SyncBehaviorType.Diff_sync,
      );
      await importSource.reload();
      while (importSource.isInProgress) {
        await new Promise((resolve) => setTimeout(resolve, 5000));
        await importSource.reload();
      }
      this.notificationsService.notifyConfirmation(
        this.intl.t('knowledge-hub.content-editor.pdf.sync.success-message'),
      );
      this.args.onSyncSuccess?.();
      this.isSyncInProgress = false;
      this.buttonLabel = this.intl.t('knowledge-hub.content-editor.pdf.sync.button.sync');
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.content-editor.pdf.sync.failure-message.sync-error'),
      );
      this.args.onSyncError?.();
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::Shared::Header::SyncButton': typeof SyncButton;
    'knowledge-hub/content-editor/shared/header/sync-button': typeof SyncButton;
  }
}
